import { useState } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import { formatDate } from './helpers';
import Text from '../../../../shared/Text';
import { DeleteMessageModal } from '../Posts/PostNotifications/DeleteMessageModal.jsx';
import { DeleteReminderModal } from '../Posts/PostNotifications/DeleteReminderModal.jsx';
import { LIVE, MESSAGE, REMINDER } from '../../utils/constants';
import { NotConfigured } from './NotConfigured.jsx';
import { ButtonBase } from '../../../../shared/Button';
import { useCampaignContext } from '../../contexts/CampaignContext.jsx';

const DateText = ({ date }) => {
  const [calendarDate, hour] = date.split(' at ');

  return (
    <DateWrapper>
      <Text variant="button">{calendarDate}</Text>
      <Text variant="body3" color="black.80">
        {hour}
      </Text>
    </DateWrapper>
  );
};

export const NotificationsPanel = ({ post, setWizardModalMode }) => {
  const { message } = post;
  const { campaign } = useCampaignContext();
  const isDisabled = campaign?.isArchived || !!campaign?.deletedAt;
  const [deleteMessageModal, setDeleteMessageModal] = useState(false);
  const [deleteReminderModal, setDeleteReminderModal] = useState(false);

  return (
    <Container>
      <Section>
        <TitleWrapper>
          <Text variant="button">Notification</Text>
          {post.message ? (
            <>
              {post.status === LIVE || isDisabled ? (
                <EyeIcon
                  data-testid="notification-view"
                  onClick={() => {
                    setWizardModalMode({
                      singleStep: true,
                      page: MESSAGE,
                      open: true,
                    });
                  }}
                />
              ) : (
                <PenIcon
                  data-testid="notification-edit"
                  disabled={post.status === LIVE || isDisabled}
                  onClick={() => {
                    setWizardModalMode({
                      singleStep: true,
                      page: MESSAGE,
                      open: true,
                    });
                  }}
                />
              )}

              <TrashIcon
                data-testid="notification-delete"
                disabled={post.status === LIVE || isDisabled}
                onClick={() => {
                  setDeleteMessageModal(true);
                }}
              />
            </>
          ) : (
            <NotConfigured />
          )}
        </TitleWrapper>
        {post.message || post.status === LIVE ? (
          <DateText
            date={formatDate(
              post.publishDate,
              post.sendTime,
              post.boardTimezone,
            )}
          />
        ) : (
          <TextAdd
            onClick={() => {
              setWizardModalMode({
                singleStep: true,
                page: MESSAGE,
                open: true,
              });
            }}
            variant="primary"
            mode="text"
            disabled={isDisabled}
          >
            <i className="fa-solid fa-plus fa-xs"></i> Notification
          </TextAdd>
        )}
      </Section>
      <Section>
        <TitleWrapper>
          <Text variant="button">Reminders</Text>
          {post.reminderDate ? (
            <>
              {post.message.reminderSent || isDisabled ? (
                <EyeIcon
                  data-testid="notification-view"
                  onClick={() => {
                    setWizardModalMode({
                      singleStep: true,
                      page: REMINDER,
                      open: true,
                    });
                  }}
                />
              ) : (
                <PenIcon
                  data-testid="reminder-edit"
                  disabled={isDisabled}
                  onClick={() => {
                    setWizardModalMode({
                      singleStep: true,
                      page: REMINDER,
                      open: true,
                    });
                  }}
                />
              )}

              <TrashIcon
                data-testid="reminder-delete"
                onClick={() => {
                  setDeleteReminderModal(true);
                }}
                disabled={post.message.reminderSent || isDisabled}
              />
            </>
          ) : (
            <NotConfigured />
          )}
        </TitleWrapper>

        {post.reminderDate ? (
          <DateText
            date={formatDate(
              post.reminderDate,
              post.reminderSendTime,
              post.boardTimezone,
            )}
          />
        ) : (
          <TextAdd
            variant="primary"
            mode="text"
            onClick={() => {
              setWizardModalMode({
                singleStep: true,
                page: REMINDER,
                open: true,
              });
            }}
            disabled={!post.message || isDisabled}
          >
            <i className="fa-solid fa-plus fa-xs"></i> Add Reminder
          </TextAdd>
        )}
      </Section>
      {message && (
        <TitleWrapper>
          <RecipientsCountWrapper>
            <Text color="black.80" variant="body3">
              Email Recipients
            </Text>
            <Text variant="button">{message.emailRecipientsCount}</Text>
          </RecipientsCountWrapper>
          <RecipientsCountWrapper>
            <Text color="black.80" variant="body3">
              Text Recipients
            </Text>
            <Text variant="button">{message.smsRecipientsCount}</Text>
          </RecipientsCountWrapper>
        </TitleWrapper>
      )}

      {/* Modals  */}
      {message && (
        <DeleteMessageModal
          open={deleteMessageModal}
          setOpen={setDeleteMessageModal}
          messageId={message.uuid}
          postId={message.postId}
          campaignId={message.campaignId}
          reminderDate={post.reminderDate}
        />
      )}
      {message && (
        <DeleteReminderModal
          open={deleteReminderModal}
          setOpen={setDeleteReminderModal}
          postId={message.postId}
          campaignId={message.campaignId}
          publishDate={post.publishDate}
        />
      )}
    </Container>
  );
};

const TextAdd = styled(ButtonBase)`
  padding: 4px;
  width: fit-content;
  height: auto;
`;

const DateWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const RecipientsCountWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Container = styled.div`
  display: flex;
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
  border-radius: 8px;
  margin: 0 20px;
  border: 1px solid ${themeGet('colors.black.10')};
`;

const Section = styled.div`
  padding: 0 0 12px;
  border-bottom: 1px solid ${themeGet('colors.black.10')};
  width: 100%;
  text-align: start;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &:last-child {
    border: none;
    padding: 0;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
`;

const PenIcon = styled.i.attrs({
  className: 'fa-regular fa-pen',
})`
  margin-left: auto;
  margin-right: 12px;
  pointer-events: ${props => (props.disabled ? 'none' : 'initial')};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? '0.5' : '1')};
`;

const TrashIcon = styled.i.attrs({
  className: 'fa-regular fa-trash-can',
})`
  pointer-events: ${props => (props.disabled ? 'none' : 'initial')};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? '0.5' : '1')};
`;

const EyeIcon = styled.i.attrs({
  className: 'fa-regular fa-eye',
})`
  margin-left: auto;
  margin-right: 12px;
  cursor: pointer;
`;
